import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';

const pageCardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
};

const dangerButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'rgba(220, 53, 69, 0.12)',
  border: '1px solid rgba(220, 53, 69, 0.25)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-error)',
  fontWeight: 600,
};

function ServiceForm({
  service,
  onCancel,
  onSave,
  saving,
}: {
  service?: any;
  onCancel: () => void;
  onSave: (data: {
    name: string;
    duration_minutes: number;
    price: number;
    category?: string;
  }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(service?.name || '');
  const [duration, setDuration] = useState(service?.duration_minutes?.toString() || '');
  const [price, setPrice] = useState(service?.price?.toString() || '');
  const [category, setCategory] = useState(service?.category || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name: name.trim(),
      duration_minutes: Number(duration),
      price: Number(price),
      category: category.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ ...pageCardStyle, marginBottom: 'var(--spacing-4)' }}>
      <div
        style={{
          display: 'grid',
          gap: 'var(--spacing-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        }}
      >
        <div>
          <label style={labelStyle}>Service Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Duration</label>
          <input
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            required
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Price</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Category</label>
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)' }}>
        <button
          type="submit"
          disabled={saving}
          style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Saving...' : service ? 'Update Service' : 'Add Service'}
        </button>
        <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function Services() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<any | null>(null);
  const [search, setSearch] = useState('');
  const [pendingDeactivate, setPendingDeactivate] = useState<any | null>(null);

  const { data: services = [], isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: api.getServices,
  });

  const createMutation = useMutation({
    mutationFn: api.createService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateService(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      setEditingService(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const filteredServices = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return services;
    return services.filter((service: any) => {
      return (
        service.name.toLowerCase().includes(term) ||
        String(service.category || '')
          .toLowerCase()
          .includes(term)
      );
    });
  }, [search, services]);

  return (
    <div className="services-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div>
          <h1>Services</h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)' }}>
            Manage the service catalogue — categories and scheduling durations. Each job is
            priced per quote.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button
            onClick={() => {
              setEditingService(null);
              setShowForm(true);
            }}
            style={primaryButtonStyle}
          >
            + Add Service
          </button>
        </div>
      </div>

      {(showForm || editingService) && (
        <ServiceForm
          service={editingService || undefined}
          saving={createMutation.isPending || updateMutation.isPending}
          onCancel={() => {
            setShowForm(false);
            setEditingService(null);
          }}
          onSave={(data) => {
            if (editingService) {
              updateMutation.mutate({ id: editingService.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
        />
      )}

      <section style={pageCardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          <h2 style={{ margin: 0 }}>Catalog</h2>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services..."
            style={{ ...inputStyle, width: '240px' }}
          />
        </div>

        {isLoading ? (
          <p style={{ color: 'var(--color-text-muted)' }}>Loading services...</p>
        ) : filteredServices.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No services found.</p>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
            {filteredServices.map((service: any) => (
              <div
                key={service.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  padding: 'var(--spacing-3)',
                  backgroundColor: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    {service.code && (
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'var(--color-accent)',
                          backgroundColor: 'rgba(212, 175, 55, 0.12)',
                          border: '1px solid rgba(212, 175, 55, 0.3)',
                          borderRadius: 4,
                          padding: '1px 6px',
                        }}
                        title="Xero item code"
                      >
                        {service.code}
                      </span>
                    )}
                    <strong>{service.name}</strong>
                  </div>
                  {service.description && (
                    <p
                      style={{
                        color: 'var(--color-text-muted)',
                        marginTop: 'var(--spacing-1)',
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      {service.description}
                    </p>
                  )}
                  <p
                    style={{
                      color: 'var(--color-text-muted)',
                      marginTop: 'var(--spacing-1)',
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    {service.category ? `${service.category} · ` : ''}
                    {service.base_rate
                      ? `$${service.base_rate}${service.unit ? ` / ${service.unit}` : ''} · `
                      : ''}
                    {service.min_charge ? `min $${service.min_charge} · ` : ''}
                    {service.duration_minutes} min slot
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                  <button onClick={() => setEditingService(service)} style={secondaryButtonStyle}>
                    Edit
                  </button>
                  <button onClick={() => setPendingDeactivate(service)} style={dangerButtonStyle}>
                    Deactivate
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={!!pendingDeactivate}
        title={`Deactivate ${pendingDeactivate?.name ?? 'service'}?`}
        message="This service will no longer be available for bookings."
        confirmLabel="Deactivate"
        onConfirm={() => {
          if (pendingDeactivate) deleteMutation.mutate(pendingDeactivate.id);
          setPendingDeactivate(null);
        }}
        onCancel={() => setPendingDeactivate(null)}
      />
    </div>
  );
}
